import { forwardRef, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import {
  LABEL_POSITIONS,
  LABEL_SIZES,
  PAPER_SAME_AS_LABEL,
  fitsOn,
  getLabelPositionFlex,
} from '@/shared/lib/label-size'
import type {
  ILabelSize,
  TLabelPosition,
  useLabelPrintSettings,
} from '@/shared/lib/label-size'
import { CustomSelect } from './select'

type TLabelPrintSettings = ReturnType<typeof useLabelPrintSettings>

const sizeLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  size: ILabelSize,
) => t('label_size.option', { width: size.widthMm, height: size.heightMm })

/**
 * «Размер этикетки», «Бумага» and «Положение» — the roll, the paper it is
 * printed on, and where on that paper the label sits. Position only matters on
 * paper larger than the label, so it is locked until such paper is chosen.
 */
export function LabelPrintSettings({
  settings,
}: {
  settings: TLabelPrintSettings
}) {
  const { t } = useTranslation()
  const { size, paperId, position, setSizeId, setPaperId, setPosition } =
    settings

  const sizeOptions = useMemo(
    () =>
      LABEL_SIZES.map((item) => ({
        label: sizeLabel(t, item),
        value: item.id,
      })),
    [t],
  )
  const paperOptions = useMemo(
    () => [
      { label: t('label_size.paper_same'), value: PAPER_SAME_AS_LABEL },
      ...LABEL_SIZES.filter(
        (item) => item.id !== size.id && fitsOn(size, item),
      ).map((item) => ({ label: sizeLabel(t, item), value: item.id })),
    ],
    [t, size],
  )
  const positionOptions = useMemo(
    () =>
      LABEL_POSITIONS.map((item) => ({
        label: t(`label_size.position.${item}`),
        value: item,
      })),
    [t],
  )

  return (
    <SimpleGrid columns={{ base: 1, sm: 3 }} gap={3}>
      <Box>
        <Text fontSize="xs" color="fg.muted" mb={1} fontWeight="medium">
          {t('label_size.title')}
        </Text>
        <CustomSelect
          options={sizeOptions}
          value={[size.id]}
          onChange={(next) => next[0] && setSizeId(next[0])}
          width="100%"
          size="sm"
        />
      </Box>
      <Box>
        <Text fontSize="xs" color="fg.muted" mb={1} fontWeight="medium">
          {t('label_size.paper')}
        </Text>
        <CustomSelect
          options={paperOptions}
          value={[paperId]}
          onChange={(next) => next[0] && setPaperId(next[0])}
          width="100%"
          size="sm"
        />
      </Box>
      <Box>
        <Text fontSize="xs" color="fg.muted" mb={1} fontWeight="medium">
          {t('label_size.position_title')}
        </Text>
        <CustomSelect<TLabelPosition>
          options={positionOptions}
          value={[position]}
          onChange={(next) => next[0] && setPosition(next[0])}
          disabled={paperId === PAPER_SAME_AS_LABEL}
          width="100%"
          size="sm"
        />
      </Box>
    </SimpleGrid>
  )
}

interface LabelSheetProps {
  paper: ILabelSize
  position: TLabelPosition
  children: ReactNode
}

/**
 * The printed page: the paper at true size with the label placed on it. This
 * is what react-to-print prints (pass its ref as `contentRef`).
 */
export const LabelSheet = forwardRef<HTMLDivElement, LabelSheetProps>(
  function LabelSheetImpl({ paper, position, children }, ref) {
    return (
      <Flex
        ref={ref}
        bg="white"
        overflow="hidden"
        w={`${paper.widthMm}mm`}
        h={`${paper.heightMm}mm`}
        {...getLabelPositionFlex(position)}
      >
        {children}
      </Flex>
    )
  },
)
